import * as React from "react";
import { useParams } from "react-router-dom";
import { useGo } from "@refinedev/core";

import type {
  ProjectControlTowerPacket,
  projectControlWindowSchema,
} from "@aos/shared";
import type { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Icon, type IconName } from "@/components/ui/icon";
import { ErrorState } from "@/components/ui/states";
import { RenderState, type RenderStateKind } from "@/components/app/RenderState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection, RefLink } from "@/components/inspector";
import { ApiError, api } from "@/lib/api";
import { formatDate, formatDateTime, humanize } from "@/lib/format";

/** The control-window options the worker accepts (mirrors `projectControlWindowSchema`). */
type ControlWindow = z.infer<typeof projectControlWindowSchema>;
const WINDOW_OPTIONS: readonly ControlWindow[] = ["now", "7d", "30d", "quarter", "custom"];
const WINDOW_LABEL: Record<ControlWindow, string> = {
  now: "Now",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  quarter: "This quarter",
  custom: "Custom",
};

/** The viewer-facing fetch state: forbidden/error are distinguished from the
 *  packet's own `surface_state` so the eight-state contract covers transport
 *  outcomes (403/410-style) as well as projection outcomes (empty/stale/…). */
type FetchPhase =
  | { kind: "loading" }
  | { kind: "forbidden"; reason: string }
  | { kind: "error"; message: string }
  | { kind: "ready"; packet: ProjectControlTowerPacket };

function useProjectControlTowerPacket(projectId: string, window: ControlWindow) {
  const [phase, setPhase] = React.useState<FetchPhase>({ kind: "loading" });
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setPhase({ kind: "loading" });
    api
      .get<ProjectControlTowerPacket>(
        `/projects/${encodeURIComponent(projectId)}/control-tower`,
        { query: { window } },
      )
      .then((packet) => {
        if (!cancelled) setPhase({ kind: "ready", packet });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          setPhase({
            kind: "forbidden",
            reason:
              "You do not have permission to view this project's control tower.",
          });
          return;
        }
        setPhase({
          kind: "error",
          message:
            err instanceof Error
              ? err.message
              : "Failed to load the Project Control Tower.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, window, reloadKey]);

  return { phase, reload };
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <p className="font-mono text-sm tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

type BadgeVariant = NonNullable<React.ComponentProps<typeof Badge>["variant"]>;

const HEALTH_VARIANT: Record<ProjectControlTowerPacket["health_status"], BadgeVariant> = {
  unknown: "secondary",
  green: "success",
  amber: "warning",
  red: "destructive",
  critical: "destructive",
};

const MOVEMENT_ICON: Record<ProjectControlTowerPacket["movement_status"], IconName> = {
  improving: "sortAsc",
  stable: "resultOk",
  degrading: "sortDesc",
  stalled: "stop",
  no_signal: "scope",
};

function HealthBadge({ status }: { status: ProjectControlTowerPacket["health_status"] }) {
  return <Badge variant={HEALTH_VARIANT[status]}>{humanize(status)}</Badge>;
}

/** A small "Object / Kind / Status / Owner" work-objects table, mirroring the
 *  spec's map-table. Read projection only — rows link to the canonical records. */
function WorkObjectsTable({ packet }: { packet: ProjectControlTowerPacket }) {
  const rows: {
    id: string;
    resource: string;
    title: string;
    kind: string;
    status: string | null;
    owner: string | null;
  }[] = [
    ...packet.deliverables.map((item) => ({
      id: item.id,
      resource: "artifacts",
      title: item.title,
      kind: item.artifact_type,
      status: item.status,
      owner: item.owner_user_id,
    })),
    ...packet.risks_and_issues.map((item) => ({
      id: item.id,
      resource: "artifacts",
      title: item.title,
      kind: item.artifact_type,
      status: item.status,
      owner: item.owner_user_id,
    })),
    ...packet.milestones.map((item) => ({
      id: item.id,
      resource: "artifacts",
      title: item.title,
      kind: item.artifact_type,
      status: item.status,
      owner: null,
    })),
    ...packet.decisions.map((item) => ({
      id: item.id,
      resource: "decisions",
      title: item.title,
      kind: "decision",
      status: item.status,
      owner: null,
    })),
  ];

  if (rows.length === 0) {
    return (
      <p className="px-1 py-2 text-sm text-muted-foreground">
        No deliverables, risks, milestones, or decisions are bound to this project yet.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Object</TableHead>
          <TableHead>Kind</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Owner</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.resource}:${row.id}`}>
            <TableCell className="[overflow-wrap:anywhere]">
              <RefLink resource={row.resource} id={row.id} label={row.title} />
            </TableCell>
            <TableCell>
              <Badge variant="outline">{humanize(row.kind)}</Badge>
            </TableCell>
            <TableCell>
              {row.status ? <StatusBadge status={row.status} /> : <span className="text-muted-foreground">—</span>}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
              {row.owner ?? "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ObjectiveCard({
  objective,
}: {
  objective: ProjectControlTowerPacket["objectives"][number];
}) {
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base [overflow-wrap:anywhere]">{objective.title}</CardTitle>
            {objective.period ? (
              <p className="mt-1 text-xs text-muted-foreground">{objective.period}</p>
            ) : null}
          </div>
          <StatusBadge status={objective.status} />
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {objective.key_results.length === 0 ? (
          <p className="text-sm text-muted-foreground">No key results.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {objective.key_results.map((kr) => (
              <li key={kr.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 [overflow-wrap:anywhere]">{kr.title}</span>
                <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                  {kr.progress_ratio === null
                    ? "no data"
                    : `${Math.round(kr.progress_ratio * 100)}%`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

const SEVERITY_VARIANT: Record<
  ProjectControlTowerPacket["recommendation_stream"][number]["severity"],
  BadgeVariant
> = { low: "outline", medium: "secondary", high: "warning", critical: "destructive" };

function RecommendationRow({
  rec,
}: {
  rec: ProjectControlTowerPacket["recommendation_stream"][number];
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/20 px-3 py-2">
      <div className="min-w-0">
        <p className="text-sm [overflow-wrap:anywhere]">{rec.title}</p>
        <p className="mt-0.5 font-mono text-xs text-muted-foreground">{humanize(rec.kind)}</p>
      </div>
      <Badge variant={SEVERITY_VARIANT[rec.severity]}>{humanize(rec.severity)}</Badge>
    </div>
  );
}

/** The populated workspace — always rendered for `normal`, and behind the
 *  projection banners (stale / overflow / redacted) by `RenderState`. */
function ControlTowerWorkspace({
  packet,
  reload,
}: {
  packet: ProjectControlTowerPacket;
  reload: () => void;
}) {
  const summary = packet.summary;
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Metric label="Deliverables" value={`${summary.accepted_deliverables}/${summary.deliverables}`} />
        <Metric label="Open risks" value={summary.open_risk_issues} />
        <Metric label="Milestones" value={summary.milestones} />
        <Metric label="Open decisions" value={summary.open_decisions} />
        <Metric label="Key results" value={summary.key_results} />
        <Metric label="Dependencies" value={summary.dependencies} />
      </div>

      <InspectorSection
        title="Project status"
        aside={
          <span className="text-xs text-muted-foreground">
            Generated {formatDateTime(packet.generated_at)}
          </span>
        }
      >
        <FieldGrid>
          <Field label="Project">
            <RefLink resource="projects" id={packet.project.id} label={packet.project.name} />
          </Field>
          <Field label="Operational">
            <StatusBadge status={packet.project.operational_status} />
          </Field>
          <Field label="Health">
            <HealthBadge status={packet.health_status} />
          </Field>
          <Field label="Movement">
            <span className="inline-flex items-center gap-1.5">
              <Icon name={MOVEMENT_ICON[packet.movement_status]} aria-hidden />
              {humanize(packet.movement_status)}
            </span>
          </Field>
          <Field label="Depth">{humanize(packet.depth)}</Field>
          <Field label="Review">{humanize(packet.review_freshness)}</Field>
          <Field label="Context">
            <RefLink
              resource="contexts"
              id={packet.project_context.id}
              label={packet.project_context.name}
            />
          </Field>
          <Field label="Lens">
            {packet.control_view.lens ? packet.control_view.lens.name : "—"}
          </Field>
          <Field label="Saved view">
            {packet.control_view.saved_view ? packet.control_view.saved_view.name : "—"}
          </Field>
          <Field label="Next review">{formatDate(packet.project_context.review_at)}</Field>
        </FieldGrid>
        {packet.integrity_warnings.length > 0 ? (
          <div className="mt-4 rounded-md border border-warning/40 bg-warning/10 px-4 py-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Icon name="events" aria-hidden />
              Integrity warnings
            </div>
            <ul className="list-inside list-disc text-sm text-warning-foreground/90">
              {packet.integrity_warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </InspectorSection>

      <InspectorSection
        title="Project work objects"
        aside={
          <Button variant="outline" size="sm" onClick={reload}>
            <Icon name="reopen" data-icon="inline-start" aria-hidden />
            Refresh
          </Button>
        }
      >
        <WorkObjectsTable packet={packet} />
      </InspectorSection>

      <InspectorSection title="Dependencies">
        {packet.dependencies.length === 0 ? (
          <p className="text-sm text-muted-foreground">No cross-context dependencies bound.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Relation</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packet.dependencies.map((dep) => (
                <TableRow key={dep.id}>
                  <TableCell>
                    <Badge variant="outline">{humanize(dep.relation)}</Badge>
                  </TableCell>
                  <TableCell className="[overflow-wrap:anywhere]">
                    <RefLink resource={`${dep.subject_type}s`} id={dep.subject_id} label={dep.title} />
                  </TableCell>
                  <TableCell>
                    {dep.status ? <StatusBadge status={dep.status} /> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </InspectorSection>

      <InspectorSection title="Objectives">
        {packet.objectives.length === 0 ? (
          <p className="text-sm text-muted-foreground">No objectives bound to this project.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {packet.objectives.map((objective) => (
              <ObjectiveCard key={objective.id} objective={objective} />
            ))}
          </div>
        )}
      </InspectorSection>

      <InspectorSection title="Recommendations">
        {packet.recommendation_stream.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recommended actions.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {packet.recommendation_stream.map((rec) => (
              <RecommendationRow key={rec.id} rec={rec} />
            ))}
          </div>
        )}
      </InspectorSection>

      <InspectorSection title="Activity">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Metric label="Recent events" value={summary.events} />
          <Metric label="Agent runs" value={summary.agent_runs} />
          <Metric label="Recommendations" value={summary.recommendations} />
        </div>
      </InspectorSection>
    </div>
  );
}

/** Map the worker `surface_state` onto the shared eight-state contract, then
 *  render the workspace under any projection banner. `loading`/`error` from the
 *  packet itself never reach here (those are fetch phases), but they are valid
 *  RenderState kinds so we pass them through faithfully. */
function ReadyView({
  packet,
  reload,
}: {
  packet: ProjectControlTowerPacket;
  reload: () => void;
}) {
  const state = packet.surface_state as RenderStateKind;
  const workspace = <ControlTowerWorkspace packet={packet} reload={reload} />;

  if (state === "empty") {
    return (
      <RenderState
        state="empty"
        empty={{
          icon: "projects",
          title: "This project has no governed work yet.",
          hint: "Add a deliverable, decision, or objective and bind it to the Project Context to populate the tower.",
        }}
      />
    );
  }

  if (state === "stale") {
    return (
      <RenderState state="stale" staleAsOf={formatDateTime(packet.generated_at)} onRetry={reload}>
        {workspace}
      </RenderState>
    );
  }

  if (state === "overflow") {
    const total =
      summaryTotal(packet) - (packet.summary.dependencies + packet.summary.events + packet.summary.agent_runs);
    return (
      <RenderState state="overflow" overflowCount={Math.max(0, total - 100)}>
        {workspace}
      </RenderState>
    );
  }

  // normal / loading / error / redacted / forbidden from the packet itself
  // (only `normal` is expected from this read-model) all render the workspace
  // with the canonical wrapper.
  return <RenderState state={state}>{workspace}</RenderState>;
}

function summaryTotal(packet: ProjectControlTowerPacket): number {
  const s = packet.summary;
  return s.deliverables + s.risk_issues + s.milestones + s.decisions + s.objectives;
}

export function ProjectControlTowerPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <ErrorState message="Missing project id." />;
  return <ProjectControlTowerView projectId={id} />;
}

function ProjectControlTowerView({ projectId }: { projectId: string }) {
  const go = useGo();
  const [window, setWindow] = React.useState<ControlWindow>("now");
  const { phase, reload } = useProjectControlTowerPacket(projectId, window);

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Project Control Tower"
          subtitle="Project-centered operating surface for deliverables, risks, milestones, decisions, objectives, and recommendations."
          icon="controlRoom"
          actions={
            <div className="flex items-center gap-2">
              <Select value={window} onValueChange={(value) => setWindow(value as ControlWindow)}>
                <SelectTrigger className="w-[150px]" aria-label="Control window">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WINDOW_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {WINDOW_LABEL[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => go({ to: `/projects/${projectId}` })}>
                <Icon name="projects" data-icon="inline-start" aria-hidden />
                Project
              </Button>
            </div>
          }
        />

        {phase.kind === "loading" ? <RenderState state="loading" loadingRows={6} /> : null}
        {phase.kind === "forbidden" ? (
          <RenderState state="forbidden" forbiddenReason={phase.reason} forbiddenApprovalPath="your project admin" />
        ) : null}
        {phase.kind === "error" ? (
          <RenderState state="error" errorMessage={phase.message} onRetry={reload} />
        ) : null}
        {phase.kind === "ready" ? <ReadyView packet={phase.packet} reload={reload} /> : null}
      </div>
    </div>
  );
}

export default ProjectControlTowerPage;
